import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, Download, RotateCcw, ScanSearch, Lock, Wallet, AlertTriangle, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const money = (value) => `${Number(value ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('tr-TR') : '-');

function Step({ index, title, done, active }) {
  const tone = done ? 'bg-green-600 text-white' : active ? 'bg-brand-neon text-brand-ink ring-1 ring-brand-ink' : 'bg-gray-200 text-gray-600';
  return (
    <div className="flex items-center gap-2">
      <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-semibold ${tone}`}>
        {done ? '✓' : index}
      </span>
      <span className={`text-sm ${active || done ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>{title}</span>
    </div>
  );
}

function Tile({ label, value, tone = 'text-gray-900' }) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-bold ${tone}`}>{value}</div>
    </div>
  );
}

/**
 * Semi-automatic payment flow for a paid campaign:
 * 1. amounts are calculated (pool formula or fixed task fee)
 * 2. admin re-checks Instagram proofs and finalizes
 * 3. admin pays by bank transfer (Excel list) and marks rows as paid; users get notified.
 */
export default function AdminPayoutPanel({ campaignId, accessToken, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [reference, setReference] = useState('');
  const [editing, setEditing] = useState(null);

  const call = useCallback(async (path, method = 'GET', body) => {
    const response = await fetch(`${BACKEND_URL}/api${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.detail || 'İşlem başarısız');
    return result;
  }, [accessToken]);

  const load = useCallback(async () => {
    if (!accessToken) return;
    try {
      setData(await call(`/admin/campaigns/${campaignId}/payouts`));
    } catch (error) {
      toast.error(error.message);
    }
  }, [accessToken, call, campaignId]);

  useEffect(() => { load(); }, [load]);

  const run = async (action, successMessage) => {
    setBusy(true);
    try {
      const result = await action();
      if (result?.rows) setData(result);
      else await load();
      setSelected(new Set());
      if (successMessage) toast.success(typeof successMessage === 'function' ? successMessage(result) : successMessage);
      onChanged?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  const { rows, summary, finalized } = data;
  const unpaidRows = rows.filter((row) => !row.paid_at);
  const selectedRows = rows.filter((row) => selected.has(row.id));
  const selectedUnpaid = selectedRows.filter((row) => !row.paid_at);
  const selectedPaid = selectedRows.filter((row) => row.paid_at);
  const allPaid = finalized && summary.count > 0 && summary.unpaid_count === 0;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllUnpaid = () => setSelected((prev) => (
    prev.size === unpaidRows.length ? new Set() : new Set(unpaidRows.map((row) => row.id))
  ));

  const exportBankList = () => {
    const source = selectedUnpaid.length ? selectedUnpaid : unpaidRows;
    // One line per person: a user with several tasks gets a single transfer.
    const byUser = new Map();
    source.forEach((row) => {
      const current = byUser.get(row.user_id) || { ...row, amount: 0, tasks: [] };
      current.amount += Number(row.payout_amount || 0);
      current.tasks.push(row.task_title);
      byUser.set(row.user_id, current);
    });
    const sheet = [...byUser.values()].map((row) => ({
      'Ad Soyad': row.full_name || '',
      IBAN: row.iban || 'EKSİK',
      'IBAN Doğrulandı': row.iban_verified ? 'Evet' : 'Hayır',
      'Tutar (TL)': Number(row.amount.toFixed(2)),
      Açıklama: `${data.campaign.title} #${row.participant_no}`,
      'E-posta': row.email || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'Ödemeler');
    XLSX.writeFile(wb, `${data.campaign.title}_odeme_listesi.xlsx`);
  };

  const saveAdjustment = () => run(
    () => call(`/admin/payouts/${editing.id}`, 'PATCH', {
      payout_amount: Number(editing.amount),
      adjustment_note: editing.note || null,
    }),
    'Tutar güncellendi',
  ).then(() => setEditing(null));

  return (
    <div className="mb-6 rounded-lg border border-brand-line bg-white p-4" data-testid="payout-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
            <Wallet className="w-5 h-5" /> Ödeme Süreci
          </h3>
          <div className="flex flex-wrap gap-4 mt-2">
            <Step index={1} title="Tutarları hesapla" done={finalized} active={!finalized} />
            <Step index={2} title="Kesinleştir" done={finalized} active={!finalized} />
            <Step index={3} title="Öde ve işaretle" done={allPaid} active={finalized && !allPaid} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => run(
              () => call(`/admin/campaigns/${campaignId}/recheck-proofs`, 'POST'),
              (r) => (r.failed?.length ? `${r.failed.length} gönderide sorun bulundu` : `${r.checked} gönderi kontrol edildi, sorun yok`),
            )}
          >
            <ScanSearch className="w-4 h-4 mr-2" /> Instagram gönderilerini yeniden kontrol et
          </Button>
          {finalized ? (
            summary.paid_count === 0 && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => run(() => call(`/admin/campaigns/${campaignId}/pricing-unfinalize`, 'POST'), 'Kesinleştirme geri alındı')}
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Kesinleştirmeyi geri al
              </Button>
            )
          ) : (
            <Button
              disabled={busy}
              onClick={() => run(() => call(`/admin/campaigns/${campaignId}/pricing-finalize`, 'POST'), 'Hakedişler kesinleştirildi')}
            >
              <Lock className="w-4 h-4 mr-2" /> Hakedişleri kesinleştir
            </Button>
          )}
        </div>
      </div>

      {!finalized ? (
        <p className="text-sm text-gray-600 rounded-md bg-gray-50 p-3">
          Havuz kampanyalarında önce gelir tutarını ve KPI değerlerini girin. Hazır olduğunuzda hakedişleri kesinleştirin;
          kesinleştirilen tutarlar kullanıcılara "ödeme bekleniyor" olarak görünür ve ödeme listesi oluşur.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Tile label={`Toplam (${summary.count} hakediş)`} value={money(summary.total)} />
            <Tile label={`Ödenen (${summary.paid_count})`} value={money(summary.paid_total)} tone="text-green-700" />
            <Tile label={`Bekleyen (${summary.unpaid_count})`} value={money(summary.unpaid_total)} tone="text-brand-ink" />
            <Tile
              label="IBAN eksik / doğrulanmamış"
              value={`${summary.missing_iban} / ${summary.unverified_iban}`}
              tone={summary.missing_iban ? 'text-red-600' : 'text-gray-900'}
            />
          </div>

          {(summary.missing_iban > 0 || summary.proof_problems > 0) && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-brand-red bg-brand-red-soft p-3 text-sm text-brand-red-deep">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                {summary.missing_iban > 0 && <div>{summary.missing_iban} kişinin IBAN bilgisi yok; bu kişilere ödeme yapılamaz.</div>}
                {summary.proof_problems > 0 && <div>{summary.proof_problems} gönderinin Instagram kontrolünde sorun var (silinmiş olabilir).</div>}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Input
              className="w-56"
              placeholder="Dekont / referans no (opsiyonel)"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
            <Button
              disabled={busy || selectedUnpaid.length === 0}
              onClick={() => run(
                () => call(`/admin/campaigns/${campaignId}/payouts/mark-paid`, 'POST', {
                  payout_ids: selectedUnpaid.map((row) => row.id),
                  payment_reference: reference || null,
                }),
                `${selectedUnpaid.length} hakediş ödendi olarak işaretlendi`,
              )}
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Seçilenleri ödendi işaretle ({selectedUnpaid.length})
            </Button>
            <Button variant="outline" disabled={busy || unpaidRows.length === 0} onClick={exportBankList}>
              <Download className="w-4 h-4 mr-2" /> Banka listesi ({selectedUnpaid.length || 'tüm bekleyenler'})
            </Button>
            {selectedPaid.length > 0 && (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => run(
                  () => call(`/admin/campaigns/${campaignId}/payouts/mark-unpaid`, 'POST', { payout_ids: selectedPaid.map((row) => row.id) }),
                  'Ödeme işareti kaldırıldı',
                )}
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Ödenmedi yap ({selectedPaid.length})
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={unpaidRows.length > 0 && selected.size === unpaidRows.length}
                      onCheckedChange={toggleAllUnpaid}
                    />
                  </TableHead>
                  <TableHead>Katılımcı</TableHead>
                  <TableHead>Görev</TableHead>
                  <TableHead>IBAN</TableHead>
                  <TableHead>Instagram</TableHead>
                  <TableHead className="text-right">Tutar</TableHead>
                  <TableHead>Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} className={row.paid_at ? 'bg-green-50/40' : ''}>
                    <TableCell>
                      <Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggle(row.id)} />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{row.full_name || '-'} <span className="text-gray-400">#{row.participant_no}</span></div>
                      <div className="text-xs text-gray-500">{row.email}</div>
                    </TableCell>
                    <TableCell className="text-sm">{row.task_title}</TableCell>
                    <TableCell className="text-xs font-mono">
                      {row.iban ? (
                        <>
                          {row.iban}
                          {!row.iban_verified && <Badge className="ml-1 bg-yellow-100 text-yellow-800 text-[10px]">doğrulanmadı</Badge>}
                        </>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Eksik</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.submitted_link ? (
                        <a href={row.submitted_link} target="_blank" rel="noopener noreferrer" className="text-brand-ink font-semibold underline-offset-2 hover:underline">
                          @{row.instagram_username || 'gönderi'}
                        </a>
                      ) : '-'}
                      {row.proof_check_status === 'failed' && <Badge className="ml-1 bg-red-100 text-red-700 text-[10px]">sorunlu</Badge>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {editing?.id === row.id ? (
                        <div className="flex flex-col items-end gap-1">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="h-8 w-28"
                            value={editing.amount}
                            onChange={(e) => setEditing({ ...editing, amount: e.target.value })}
                          />
                          <Input
                            className="h-8 w-40"
                            placeholder="Not (opsiyonel)"
                            value={editing.note}
                            onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                          />
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Vazgeç</Button>
                            <Button size="sm" disabled={busy || editing.amount === ''} onClick={saveAdjustment}>Kaydet</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <span className="font-medium">{money(row.payout_amount)}</span>
                          {!row.paid_at && (
                            <button
                              type="button"
                              className="text-gray-400 hover:text-gray-700"
                              title="Tutarı düzelt"
                              onClick={() => setEditing({ id: row.id, amount: row.payout_amount ?? 0, note: row.adjustment_note || '' })}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                      {row.calculated_amount != null && row.calculated_amount !== row.payout_amount && (
                        <div className="text-[10px] text-gray-500">Hesaplanan: {money(row.calculated_amount)}</div>
                      )}
                      {row.adjustment_note && <div className="text-[10px] text-gray-500">{row.adjustment_note}</div>}
                    </TableCell>
                    <TableCell>
                      {row.paid_at ? (
                        <div>
                          <Badge className="bg-green-100 text-green-800">Ödendi</Badge>
                          <div className="text-[10px] text-gray-500 mt-1">
                            {formatDate(row.paid_at)}{row.payment_reference ? ` · ${row.payment_reference}` : ''}
                          </div>
                        </div>
                      ) : (
                        <Badge className="bg-brand-yellow-soft text-brand-ink">Ödeme bekliyor</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
